import { UseComboboxGetItemPropsOptions } from 'downshift';

import { GenericItem } from '../types';

type UseDisabledItemsProps<TItem extends GenericItem> = {
  items: TItem[];
  isItemDisabled: (item: TItem, index: number) => boolean;
};

/**
 * Used by internal Dropdown hooks to handle the return value of useDisabledItems.
 * Wrapper for getItemProps which checks for the "disabled" property on the given item.
 * This property is added by the useDisabledItems hook.
 * If the item's "disabled" prop is provided it will set the "disabled" for getItemProps.
 * This prevents selection of disabled items.
 * @param getItemProps prop getter returned from downshift hook
 * @returns modified version of getItemProps
 */
export const useGetItemProps =
  <TItem extends GenericItem>({
    getItemProps,
  }: {
    // any comes from downshift typings
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getItemProps: (options: UseComboboxGetItemPropsOptions<TItem>) => any;
  }) =>
  ({ item, disabled, ...rest }: UseComboboxGetItemPropsOptions<TItem>) =>
    getItemProps({
      item,
      disabled: disabled !== undefined ? disabled : item.disabled,
      ...rest,
    });

/**
 * A hook used to manage all logic needed for disabled menu items.  This hook is not
 * required if you add a "disabled" property to your disabled items.  If you are also
 * using useGroupedItems, useDisabledItems must come after and accept its returned "items".
 */
export default <TItem extends GenericItem>({
  items,
  isItemDisabled,
}: UseDisabledItemsProps<TItem>): {
  items: (TItem & { disabled?: boolean })[];
} => {
  for (let i = 0; i < items.length; i++) {
    if (items[i].disabled) {
      continue;
    }

    if (isItemDisabled(items[i], i)) {
      items[i].disabled = true;
    }
  }

  return {
    items,
  };
};
